import { DetailPageActionTypes } from './constants'
import { Action, Dispatch } from 'redux'
import { store } from '../store'
import { Logger } from 'DVWebNodeUtils'
import { purchaseSelector } from '../selectors/purchase'
import {
  refreshMainActionBox,
  refreshBtfActionBoxes,
  refreshEpisode,
} from './wps'
import { safeBoolean, COUNTER_PACKAGE_PREFIX } from '../utils/utils'
import { DetailPageActions } from './index'
import { whisperCacheDataSelector } from '../selectors/player'
import { auiModules } from '../utils/promises'
import { Player, PrepareConfig } from '../integrations/playerIntegrationAui'

const LOG_PREFIX = '[Purchase]'

export interface ITriggerPurchase extends Action {
  type: typeof DetailPageActionTypes.TriggerPurchase
  payload: {
    offerToken: string
    continueToken?: string
    refTag?: string
  }
}

export interface IPurchaseCancelled extends Action {
  type: typeof DetailPageActionTypes.PurchaseCancelled
}

export interface IPurchaseFailed extends Action {
  type: typeof DetailPageActionTypes.PurchaseFailed
  payload: {
    reload: boolean
  }
}

export interface CallbackInput {
  dispatch: Dispatch<DetailPageActions>
  logger: Logger
}

export const triggerPurchase = (
  dispatch: Dispatch<DetailPageActions>,
  logger: Logger,
  offerToken: string,
  continueToken?: string,
  refTag?: string
) => {
  logger
    .info(`${LOG_PREFIX} - Initiating purchase of offerToken ${offerToken}`)
    .count(
      `${COUNTER_PACKAGE_PREFIX}_Purchase_${
        continueToken ? 'continue' : 'start'
      }Purchase`,
      1,
      false
    )
  dispatch({
    type: DetailPageActionTypes.TriggerPurchase,
    payload: {
      offerToken,
      continueToken,
      refTag,
    },
  })
}

export const purchaseCancelled = ({ dispatch }: CallbackInput) =>
  dispatch({ type: DetailPageActionTypes.PurchaseCancelled })

export const purchaseFailed = ({
  dispatch,
  reload = false,
}: CallbackInput & { reload?: boolean }) =>
  dispatch({
    type: DetailPageActionTypes.PurchaseFailed,
    payload: { reload },
  })

export const refreshAfterDone = async ({ logger }: CallbackInput) => {
  const state = store.getState()
  const { pageTitleId } = state
  const purchaseDetails = purchaseSelector(state)
  if (!purchaseDetails) {
    logger
      .fatal(`${LOG_PREFIX} - No ongoing purchase found`)
      .count(`${COUNTER_PACKAGE_PREFIX}_Purchase_refreshAfterDoneError`, 1, true)
  }

  const promises = []

  const details = purchaseDetails && purchaseDetails.details
  promises.push(refreshMainActionBox(pageTitleId))
  if (!details || safeBoolean(details.offer.purchaseData.isSeasonPurchase)) {
    promises.push(refreshBtfActionBoxes(pageTitleId))
  } else {
    promises.push(refreshEpisode(details.canonicalId))
  }

  await Promise.all(promises)

  // Re-prepare player after purchase to update whisper cache
  const stateAfterPurchase = store.getState()
  const useNodePlayer = state.features.usePlayer

  if (!useNodePlayer) {
    const whisperCacheData = whisperCacheDataSelector(stateAfterPurchase)

    const modules = await auiModules({
      moduleNames: ['dv-player-integration'],
      timeout: 60000,
      logger,
    })

    const auiPlayer = modules && (modules[0] as Player)
    auiPlayer &&
      whisperCacheData &&
      auiPlayer.prepare({
        ...whisperCacheData,
        useFullView: true,
      } as PrepareConfig)
  }
}

let lastOfferTokenPurchased: string | null = null
export const refreshCSRF = async ({ dispatch, logger }: CallbackInput) => {
  const { pageTitleId, purchase } = store.getState()

  if (!purchase) {
    return
  }

  const { offerToken, continueToken, refTag } = purchase
  if (offerToken !== lastOfferTokenPurchased) {
    lastOfferTokenPurchased = offerToken
    await Promise.all([
      refreshMainActionBox(pageTitleId),
      refreshBtfActionBoxes(pageTitleId),
    ])
    triggerPurchase(dispatch, logger, offerToken, continueToken, refTag)
  } else {
    //Protection to avoid entering into an endless callback loop
    logger
      .fatal(
        `${LOG_PREFIX} - CSRF Callback already called once for offerToken ${offerToken}`
      )
      .count(`${COUNTER_PACKAGE_PREFIX}_Purchase_refreshCSRFError`, 1, true)
    lastOfferTokenPurchased = null
    purchaseFailed({ dispatch, logger, reload: true })
  }
}
